import { Injectable } from "@angular/core";
import { isEmpty } from "lodash";
import { CandidateRole } from "../models/enum";
import { ReportSummaryComment } from "../models/models";

@Injectable({
	providedIn: "root",
})
export class HelperService {
	constructor() {}

	/**
	 * Converts buffer data into image url
	 * @param bufferData
	 * @returns string
	 */
	bufferDataToImageUrl(bufferData?: any): string {
		if (!isEmpty(bufferData)) {
			const uint8Array = new Uint8Array(bufferData);
			let binary = "";
			uint8Array.forEach((byte) => {
				binary += String.fromCharCode(byte);
			});
			const base64String = btoa(binary);

			// Construct the data URL
			const dataUrl = `data:image/png;base64,${base64String}`;

			return dataUrl;
		} else {
			return "";
		}
	}

	/**
	 * Converts buffer data into image File
	 * @param bufferData
	 * @param fileName
	 * @returns
	 */
	bufferDataToFile(bufferData?: any): File | undefined {
		if (isEmpty(bufferData)) {
			return undefined;
		}

		const fileName = `siganture-${new Date().getTime()}.png`;

		// Convert buffer data to a Blob
		const uint8Array = new Uint8Array(bufferData);
		const blob = new Blob([uint8Array], { type: "image/png" });

		// Create a File object from the Blob
		const file = new File([blob], fileName, { type: "image/png" });

		return file;
	}

	isEmptyComment(comment: string): boolean {
		const value = comment.split(": ")[1];

		return isEmpty(value);
	}

	isTEM(comment: ReportSummaryComment) {
		return comment && comment.status.includes("TEM");
	}

	getRate(comment: ReportSummaryComment): number {
		const status = comment.status.toLowerCase();

		if (status.includes("incomplete")) {
			return 0;
		}

		if (status.includes("n/a")) {
			return -1;
		}

		if (status.includes("tem") && status.includes("rate")) {
			const rate = comment.status.toUpperCase().replace(/.*RATE : (\d+).*/, "$1");

			const rateNum = parseInt(rate);

			return rateNum;
		} else {
			return 0;
		}
	}

	isNaComment(comment: ReportSummaryComment): boolean {
		if (!comment) return false;
		if (comment.isNa === true) return true;
		const status = comment.status ?? "";
		return /\{\s*N\/A\s*\}/i.test(status) && !/\{\s*Undo\s+N\/A\s*\}/i.test(status);
	}

	isUndoNaComment(comment: ReportSummaryComment): boolean {
		if (!comment) return false;
		if (comment.undoNa === true) return true;
		return /\{\s*Undo\s+N\/A\s*\}/i.test(comment.status ?? "");
	}

	isUndoComment(comment: ReportSummaryComment): boolean {
		if (!comment) return false;
		if (comment.isReset === true && !comment.undoNa) return true;
		const status = comment.status ?? "";
		return /\{\s*Undo\s*\}/i.test(status) && !this.isUndoNaComment(comment);
	}

	/** Undo or undo-after-N/A (legacy name kept for templates). */
	isResetComment(comment: ReportSummaryComment): boolean {
		if (!comment) return false;
		if (this.isUndoComment(comment) || this.isUndoNaComment(comment)) return true;
		return (comment.status ?? "").toLowerCase().includes("reset");
	}

	isRepeatOnlyComment(comment: ReportSummaryComment): boolean {
		return !!comment?.isRepeat && !comment.isAdd && !comment.isRemove;
	}

	/** Orange/yellow highlight: N/A, Undo, Undo N/A, Repeat. */
	isSummaryHighlightStatus(comment: ReportSummaryComment): boolean {
		if (!comment) return false;
		return (
			this.isRepeatOnlyComment(comment) ||
			this.isNaComment(comment) ||
			this.isUndoComment(comment) ||
			this.isUndoNaComment(comment)
		);
	}

	/** Non-TEM status that is not add / remove / repeat / N/A / undo (shown as incomplete). */
	isSummaryStatusIncomplete(comment: ReportSummaryComment): boolean {
		if (!comment || this.isTEM(comment)) return false;
		if (comment.isAdd || comment.isRemove) return false;
		if (this.isSummaryHighlightStatus(comment)) return false;
		return true;
	}

	/** Match live rating form summary: drop undo rows and remove the row they revert. */
	filterReportSummaryComments(comments: ReportSummaryComment[]): ReportSummaryComment[] {
		if (!comments?.length) return [];

		const sorted = [...comments].sort(
			(a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
		);
		const result: ReportSummaryComment[] = [];

		const maneuverText = (c: ReportSummaryComment) => (c.maneuver ?? c.summary ?? "").trim();

		for (const comment of sorted) {
			if (this.isUndoComment(comment) || this.isUndoNaComment(comment) || comment.isReset) {
				const display = this.toSummaryDisplayComment(comment);
				const targetManeuver = maneuverText(comment);

				for (let i = result.length - 1; i >= 0; i--) {
					const rowDisplay = this.toSummaryDisplayComment(result[i]);
					if (rowDisplay.ob === "REPEAT") continue;
					if (rowDisplay.ob !== display.ob) continue;
					if (maneuverText(result[i]) !== targetManeuver) continue;
					result.splice(i, 1);
					break;
				}
				continue;
			}

			result.push(comment);
		}

		return result;
	}

	toSummaryDisplayComment(comment: ReportSummaryComment) {
		if (comment?.maneuver != null && comment?.ob != null) {
			return {
				candidateRole: comment.candidateRole ?? null,
				maneuver: comment.maneuver,
				ob: comment.ob,
				action: comment.action ?? null,
				instructorComment: comment.instructorComment ?? comment.comment ?? "",
				isDisabled: comment.isNa ?? false,
				isReset: comment.isReset,
				lastActionDisabled: comment.undoNa,
				rate: comment.rate ?? this.getRate(comment),
				remarks: comment.remarks,
			};
		}

		const isRepeatOnly = this.isRepeatOnlyComment(comment);
		let ob = "";
		let action: boolean | null = null;
		let candidateRole: string | null = null;

		if (isRepeatOnly) {
			ob = "REPEAT";
		} else if (this.isTEM(comment)) {
			ob = "TEM";
		} else {
			const status = comment.status ?? "";
			const obMatch = status.match(/^(.+?)\s*(?:\[|\{)/);
			ob = obMatch?.[1]?.trim() ?? "";
			if (comment.isAdd) action = true;
			if (comment.isRemove) action = false;
		}

		const roleMatch = comment.status?.match(/\[\s*(PF|PM)\s*\]\s*$/);
		if (roleMatch) {
			candidateRole = roleMatch[1] === "PF" ? CandidateRole.PF : CandidateRole.PM;
		}

		return {
			candidateRole,
			maneuver: comment.summary ?? "",
			ob,
			action,
			instructorComment: comment.comment ?? "",
			isDisabled: comment.isNa ?? false,
			isReset: comment.isReset,
			lastActionDisabled: comment.undoNa,
			rate: comment.rate ?? this.getRate(comment),
			remarks: comment.remarks,
		};
	}

	getStatus(comment: ReportSummaryComment): string {
		const status = comment.status.toLowerCase();

		if (status.includes("tem") && status.includes("rate")) {
			const rate = status.toUpperCase().replace(/.*RATE : (\d+).*/, "$1");

			const rateNum = parseInt(rate);

			if (rateNum >= 1 && rateNum <= 5) {
				return `TEM { Rating: ${rateNum} }`;
			} else {
				return `TEM { Incomplete }`;
			}
		}

		if (this.isRepeatOnlyComment(comment)) {
			return "Repeat";
		}

		return comment.status.replace(/reset/gi, "Undo");
	}
}
